// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';

import { api } from '../api/client';
import type { SupabaseAccount, SupabaseAccountUser } from '../api/supabase';
import type { SpeckleTokenStatus } from '../api/types';
import { useAuth } from '../app/AuthGate';
import { supabaseAccount } from '../app/sessionRuntime';
import { useArmedConfirm } from '../app/useArmedConfirm';
import { useAsync } from '../app/useAsync';
import { Button } from '../components/Button';
import { Select } from '../components/Select';
import { Spinner } from '../components/Spinner';
import { TextInput } from '../components/TextInput';
import { TextLink } from '../components/TextLink';
import controls from '../components/controls.module.css';
import {
  accountErrorText,
  emailChangeCheck,
  MIN_PASSWORD_LENGTH,
  passwordChangeCheck,
  usernameCheck,
  type Check,
} from '../konto/model';
import {
  canRemoveSpeckleToken,
  DEFAULT_SPECKLE_SERVER,
  initialSpeckleServer,
  isLocalSpeckle,
  SPECKLE_OPTIONAL_SCOPE,
  SPECKLE_REQUIRED_SCOPES,
  speckleErrorText,
  speckleHost,
  speckleStatusText,
  speckleTokenCheck,
  speckleTokensLink,
} from '../konto/speckle';
import styles from './KontoPage.module.css';

/**
 * Konto — the signed-in user's own account (ruxd-wmc.5, frontend-gm1): name,
 * e-mail and password. Supabase owns accounts, so every change goes straight
 * to Supabase with supabase-js; ruxd only reads the result. Local mode has no
 * accounts at all. The Speckle panel (frontend-ayw.5) talks to ruxd instead
 * (`/me/speckle`) and shows in both modes.
 */
export function KontoPage() {
  const auth = useAuth();
  const account = supabaseAccount();
  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1 className={styles.title}>Konto</h1>
      </header>
      {auth?.me.mode !== 'server' || !account ? (
        <section className={styles.panel} aria-label="Konto">
          <p className={styles.text}>
            {auth?.me.mode === 'server'
              ? 'Login-tjenesten er ikke sat op i denne browser, så kontoen kan ikke ændres herfra.'
              : 'Serveren kører i lokal tilstand: én person, intet login og ingen konto at ændre. Speckle-forbindelsen sættes op herunder.'}
          </p>
        </section>
      ) : (
        <AccountPanels account={account} />
      )}
      <SpecklePanel />
    </div>
  );
}

function AccountPanels({ account }: { account: SupabaseAccount }) {
  const [user, setUser] = useState<SupabaseAccountUser | null | undefined>(undefined);
  useEffect(() => {
    let live = true;
    account.currentUser().then(
      (u) => live && setUser(u),
      () => live && setUser(null),
    );
    return () => {
      live = false;
    };
  }, [account]);

  if (user === undefined) return <Spinner label="Indlæser konto…" />;
  if (user === null)
    return (
      <section className={styles.panel}>
        <p className={styles.error} role="alert">
          Kontoen kunne ikke hentes. Log ind igen, og prøv så igen.
        </p>
      </section>
    );

  return (
    <>
      <NamePanel account={account} user={user} onSaved={setUser} />
      <EmailPanel account={account} user={user} onSaved={setUser} />
      <PasswordPanel account={account} user={user} />
    </>
  );
}

interface PanelProps {
  account: SupabaseAccount;
  user: SupabaseAccountUser;
  onSaved: (user: SupabaseAccountUser) => void;
}

/** One form panel: a heading, its fields, one submit, and its own result line. */
function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  async function run<T>(check: Check<T>, action: (value: T) => Promise<string>) {
    if (busy) return;
    if (!check.ok) {
      setResult({ ok: false, text: check.reason });
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      setResult({ ok: true, text: await action(check.value) });
    } catch (cause) {
      setResult({ ok: false, text: accountErrorText(cause) });
    } finally {
      setBusy(false);
    }
  }
  return { busy, result, run };
}

function Panel({ title, intro, children }: { title: string; intro?: ReactNode; children: ReactNode }) {
  const headingId = useId();
  return (
    <section className={styles.panel} aria-labelledby={headingId}>
      <h2 id={headingId} className={styles.panelHeading}>
        {title}
      </h2>
      {intro && <p className={styles.muted}>{intro}</p>}
      {children}
    </section>
  );
}

function ResultLine({ result }: { result: { ok: boolean; text: string } | null }) {
  if (!result) return null;
  return result.ok ? (
    <p className={styles.ok} role="status">
      {result.text}
    </p>
  ) : (
    <p className={styles.error} role="alert">
      {result.text}
    </p>
  );
}

function NamePanel({ account, user, onSaved }: PanelProps) {
  const [name, setName] = useState(user.username);
  const { busy, result, run } = useSubmit();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(usernameCheck(name, user.username), async (username) => {
      onSaved(await account.updateUser({ username }));
      return username === '' ? 'Navnet er fjernet — din e-mail vises i stedet.' : 'Navnet er gemt.';
    });
  };
  return (
    <Panel title="Navn" intro="Vises i titellinjen og i sagernes medlemslister. Uden navn vises din e-mail.">
      <form className={styles.row} onSubmit={submit}>
        <label className={controls.field}>
          <span className={controls.fieldLabel}>Navn</span>
          <TextInput
            variant="outline"
            size="sm"
            block
            value={name}
            autoComplete="name"
            placeholder={user.email}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <Button type="submit" variant="primary" size="sm" disabled={busy}>
          Gem navn
        </Button>
      </form>
      <ResultLine result={result} />
    </Panel>
  );
}

function EmailPanel({ account, user, onSaved }: PanelProps) {
  const [email, setEmail] = useState('');
  const { busy, result, run } = useSubmit();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(emailChangeCheck(email, user.email), async (next) => {
      onSaved(await account.updateUser({ email: next }));
      setEmail('');
      return `Bekræftelsen er sendt. Din e-mail skifter til ${next}, når skiftet er bekræftet fra mailen.`;
    });
  };
  return (
    <Panel
      title="E-mail"
      intro={
        <>
          Du logger ind med <span className="mono">{user.email}</span>. En ny adresse gælder først, når den er
          bekræftet fra mailen.
        </>
      }
    >
      {user.pendingEmail && (
        <p className={styles.muted}>
          Venter på bekræftelse: <span className="mono">{user.pendingEmail}</span>
        </p>
      )}
      <form className={styles.row} onSubmit={submit}>
        <label className={controls.field}>
          <span className={controls.fieldLabel}>Ny e-mail</span>
          <TextInput
            variant="outline"
            size="sm"
            block
            type="email"
            value={email}
            autoComplete="email"
            placeholder="navn@firma.dk"
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <Button type="submit" variant="primary" size="sm" disabled={busy || email.trim() === ''}>
          Skift e-mail
        </Button>
      </form>
      <ResultLine result={result} />
    </Panel>
  );
}

function PasswordPanel({ account, user }: Omit<PanelProps, 'onSaved'>) {
  const [draft, setDraft] = useState({ current: '', next: '', repeat: '' });
  const { busy, result, run } = useSubmit();
  const set = (key: keyof typeof draft) => (e: { target: { value: string } }) =>
    setDraft((d) => ({ ...d, [key]: e.target.value }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(passwordChangeCheck(draft), async (password) => {
      // Re-prove the current password first: an open browser alone must not
      // be enough to lock the account's owner out.
      await account.verifyPassword(user.email, draft.current);
      await account.updateUser({ password });
      setDraft({ current: '', next: '', repeat: '' });
      return 'Adgangskoden er skiftet.';
    });
  };
  return (
    <Panel title="Adgangskode" intro={`Mindst ${MIN_PASSWORD_LENGTH} tegn. Du skal skrive den nuværende først.`}>
      <form className={styles.stack} onSubmit={submit}>
        {/* For password managers: which account these fields belong to. */}
        <input type="email" value={user.email} autoComplete="username" readOnly hidden />
        <label className={controls.field}>
          <span className={controls.fieldLabel}>Nuværende adgangskode</span>
          <TextInput
            variant="outline"
            size="sm"
            block
            type="password"
            value={draft.current}
            autoComplete="current-password"
            onChange={set('current')}
          />
        </label>
        <div className={styles.row}>
          <label className={controls.field}>
            <span className={controls.fieldLabel}>Ny adgangskode</span>
            <TextInput
              variant="outline"
              size="sm"
              block
              type="password"
              value={draft.next}
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
              onChange={set('next')}
            />
          </label>
          <label className={controls.field}>
            <span className={controls.fieldLabel}>Gentag ny adgangskode</span>
            <TextInput
              variant="outline"
              size="sm"
              block
              type="password"
              value={draft.repeat}
              autoComplete="new-password"
              onChange={set('repeat')}
            />
          </label>
        </div>
        <div>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={busy || draft.current === '' || draft.next === ''}
          >
            {busy ? 'Skifter…' : 'Skift adgangskode'}
          </Button>
        </div>
      </form>
      <ResultLine result={result} />
    </Panel>
  );
}

/**
 * Speckle — your personal access token, so ruxd can import models from
 * Speckle (ruxd-fk8.4). ruxd checks it against Speckle before storing it and
 * never hands it back: the panel shows `…abcd` at most. The token lives only
 * in the input until it is sent. The input is cleared once it is saved, and
 * deliberately kept after a failed save (a refusal, 429, 502, …) so the user
 * can retry or fix it without pasting it again.
 */
function SpecklePanel() {
  const loaded = useAsync<SpeckleTokenStatus>((signal) => api.speckleToken(signal), []);
  const [saved, setSaved] = useState<SpeckleTokenStatus | null>(null);
  const status = saved ?? loaded.data ?? null;
  const intro =
    'Et personligt adgangstoken fra Speckle, så modeller (fx Revit-planer) kan hentes derfra. Tokenet tjekkes hos Speckle, når du gemmer, og vises aldrig igen — kun de sidste fire tegn.';

  if (!status) {
    return (
      <Panel title="Speckle" intro={intro}>
        {loaded.error ? (
          <div className={styles.row}>
            <p className={styles.error} role="alert">
              {speckleErrorText(loaded.error, 'load')}
            </p>
            <Button variant="outline" size="sm" onClick={loaded.reload}>
              Prøv igen
            </Button>
          </div>
        ) : (
          <Spinner label="Henter Speckle-forbindelsen…" />
        )}
      </Panel>
    );
  }
  return (
    <Panel title="Speckle" intro={intro}>
      <SpeckleForm status={status} onChange={setSaved} />
    </Panel>
  );
}

function SpeckleForm({ status, onChange }: { status: SpeckleTokenStatus; onChange: (s: SpeckleTokenStatus) => void }) {
  const local = isLocalSpeckle(status);
  const allowed = status.allowed_servers ?? [];
  const [token, setToken] = useState('');
  const [server, setServer] = useState(() => initialSpeckleServer(status));
  const [busy, setBusy] = useState<'save' | 'remove' | null>(null);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const confirm = useArmedConfirm<'remove'>(busy !== null, status);
  const text = speckleStatusText(status);

  // Where to make a token: the chosen server, or (local mode) the default while the typed URL is not one yet.
  // Validated like any server string before it is an href; null hides the link.
  const tokensLink = local
    ? (speckleTokensLink(server) ?? speckleTokensLink(DEFAULT_SPECKLE_SERVER))
    : speckleTokensLink(server || status.server);
  const noServers = !local && allowed.length === 0;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const check = speckleTokenCheck({ token, server }, status);
    if (!check.ok) {
      setResult({ ok: false, text: check.reason });
      return;
    }
    setBusy('save');
    setResult(null);
    try {
      const next = await api.setSpeckleToken(check.value);
      setToken('');
      onChange(next);
      setResult({
        ok: true,
        text: next.account ? `Tokenet er gemt. Forbundet som ${next.account.name}.` : 'Tokenet er gemt.',
      });
    } catch (cause) {
      setResult({ ok: false, text: speckleErrorText(cause, 'save') });
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    confirm.disarm();
    if (busy) return;
    setBusy('remove');
    setResult(null);
    try {
      await api.deleteSpeckleToken();
      // The answer is a bare 204: read back what applies now (local mode may fall back to ruxd's configured token).
      const next = await api.speckleToken();
      onChange(next);
      setResult({
        ok: true,
        text: next.source === 'config' ? 'Tokenet er fjernet. Serverens konfigurerede token gælder igen.' : 'Tokenet er fjernet.',
      });
    } catch (cause) {
      setResult({ ok: false, text: speckleErrorText(cause, 'remove') });
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div className={styles.status} data-configured={status.configured || undefined}>
        <div className={styles.statusText}>
          <p className={styles.statusHeadline}>
            <span className={styles.statusDot} aria-hidden="true" />
            {text.headline}
          </p>
          {text.meta.length > 0 && (
            <p className={styles.statusMeta}>
              {text.meta.map((item, i) => (
                <span key={item}>
                  {i > 0 && ' · '}
                  <span className={styles.nowrap}>{item}</span>
                </span>
              ))}
            </p>
          )}
        </div>
        {canRemoveSpeckleToken(status) &&
          (confirm.armed === 'remove' ? (
            <Button
              variant="outline"
              tone="danger"
              size="sm"
              armed
              disabled={busy !== null}
              onBlur={confirm.disarm}
              onClick={() => void remove()}
            >
              Fjern token?
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              disabled={busy !== null}
              onClick={(e) => confirm.arm('remove', e.currentTarget)}
            >
              {busy === 'remove' ? 'Fjerner…' : 'Fjern'}
            </Button>
          ))}
      </div>
      {text.warning && (
        <p className={styles.warn} role="status">
          {text.warning}
        </p>
      )}
      {text.sourceHint && <p className={styles.muted}>{text.sourceHint}</p>}

      {noServers ? (
        <p className={styles.muted}>
          Serveren tillader ingen Speckle-servere endnu, så der kan ikke gemmes et token. En administrator tillader dem
          med <span className="mono">--speckle-allowed-server</span>.
        </p>
      ) : (
        <form className={styles.stack} onSubmit={(e) => void save(e)} autoComplete="off" noValidate>
          <div className={styles.row}>
            {local ? (
              <label className={controls.field}>
                <span className={controls.fieldLabel}>Speckle-server</span>
                <TextInput
                  variant="outline"
                  size="sm"
                  block
                  type="text"
                  value={server}
                  inputMode="url"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder={DEFAULT_SPECKLE_SERVER}
                  disabled={busy !== null}
                  onChange={(e) => setServer(e.target.value)}
                />
              </label>
            ) : allowed.length > 1 ? (
              <label className={controls.field}>
                <span className={controls.fieldLabel}>Speckle-server</span>
                <Select
                  variant="outline"
                  size="sm"
                  value={server}
                  disabled={busy !== null}
                  onChange={(e) => setServer(e.target.value)}
                >
                  {allowed.map((s) => (
                    <option key={s} value={s}>
                      {speckleHost(s)}
                    </option>
                  ))}
                </Select>
              </label>
            ) : null}
            <label className={controls.field}>
              <span className={controls.fieldLabel}>
                {status.configured ? 'Nyt personligt adgangstoken' : 'Personligt adgangstoken'}
              </span>
              <TextInput
                variant="outline"
                size="sm"
                block
                type="password"
                value={token}
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                data-1p-ignore=""
                data-lpignore="true"
                maxLength={1024}
                placeholder="Indsæt token"
                disabled={busy !== null}
                onChange={(e) => setToken(e.target.value)}
              />
            </label>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              aria-busy={busy === 'save' || undefined}
              disabled={busy !== null || token.trim() === ''}
            >
              Gem
            </Button>
          </div>
          {busy === 'save' && <Spinner label="Tjekker tokenet hos Speckle…" />}
          <p className={styles.hint}>
            {tokensLink ? (
              <>
                Lav et personligt adgangstoken under{' '}
                <TextLink href={tokensLink.href} target="_blank" rel="noreferrer noopener">
                  {tokensLink.host}
                </TextLink>{' '}
              </>
            ) : (
              'Lav et personligt adgangstoken i Speckle '
            )}
            med adgangene{' '}
            {SPECKLE_REQUIRED_SCOPES.map((scope, i) => (
              <span key={scope}>
                {i > 0 && ' og '}
                <span className="mono">{scope}</span>
              </span>
            ))}{' '}
            (<span className="mono">{SPECKLE_OPTIONAL_SCOPE}</span> er valgfri — den viser kun dit Speckle-navn her). Et
            »Fully managed«-token virker også.
            {local &&
              status.source !== 'runtime' &&
              ' I lokal tilstand holdes tokenet kun i hukommelsen, indtil ruxd genstarter.'}
          </p>
        </form>
      )}
      <ResultLine result={result} />
    </>
  );
}
